import { Badge } from '@postscript/components';
import { ComponentProps } from 'react';
import { ProposalAudience } from './proposalAudience';
import {
  OfferProposal,
  ProposalVerification,
  ProposalVerificationTier,
} from './types';

export const completionReasonPresentation = (
  reason?: string | null,
): string => {
  if (!reason) return '—';
  if (reason === 'no_new_candidates') return 'No more pages to inspect';
  return reason
    .split('_')
    .filter(Boolean)
    .map((part) => `${part.charAt(0).toUpperCase()}${part.slice(1)}`)
    .join(' ');
};

export const sentenceCase = (value: string): string => {
  const words = value.split('_').filter(Boolean).join(' ').toLowerCase();
  return `${words.charAt(0).toUpperCase()}${words.slice(1)}`;
};

export const REVIEW_SCOPE_LABELS = {
  thisScan: 'This scan',
  allPending: 'All pending',
} as const;

type BadgeVariant = NonNullable<ComponentProps<typeof Badge>['variant']>;

interface BadgePresentation {
  label: string;
  variant: BadgeVariant;
}

const VERIFICATION_TIER_PRESENTATION: Record<
  ProposalVerificationTier,
  BadgePresentation
> = {
  1: { label: 'Shopify discount', variant: 'success' },
  2: { label: 'Corroborated', variant: 'info' },
  3: { label: 'Prominent', variant: 'warning' },
};

const UNVERIFIED_PRESENTATION: BadgePresentation = {
  label: 'Unverified',
  variant: 'neutral',
};

export const NEEDS_REVIEW_LABEL = 'Needs review';

const MERCHANT_VERIFICATION_TIER_PRESENTATION: Record<
  ProposalVerificationTier,
  BadgePresentation
> = {
  1: { label: 'Matches a Shopify discount', variant: 'success' },
  2: { label: 'Set in your theme', variant: 'info' },
  3: { label: 'Seen on your storefront', variant: 'neutral' },
};

const MERCHANT_UNVERIFIED_PRESENTATION: BadgePresentation = {
  label: NEEDS_REVIEW_LABEL,
  variant: 'warning',
};

export const verificationTierPresentation = (
  tier?: ProposalVerificationTier | null,
  audience: ProposalAudience = 'staff',
): BadgePresentation => {
  if (audience === 'merchant') {
    return (
      (tier ? MERCHANT_VERIFICATION_TIER_PRESENTATION[tier] : undefined) ??
      MERCHANT_UNVERIFIED_PRESENTATION
    );
  }
  return (
    (tier ? VERIFICATION_TIER_PRESENTATION[tier] : undefined) ??
    UNVERIFIED_PRESENTATION
  );
};

export const hasVerificationConflicts = (proposal: OfferProposal): boolean =>
  (proposal.verification?.conflicts ?? []).length > 0;

/** Where the offer was promoted, e.g. "Sitewide sale · Announcement". */
export const programmeSummary = (
  verification?: ProposalVerification | null,
): string | null => {
  const parts = [verification?.programmeKind, verification?.surface].filter(
    (part): part is string => Boolean(part),
  );
  return parts.length ? parts.map(sentenceCase).join(' · ') : null;
};

/** A reviewer's own description wins over the generated one. */
export const proposalDescription = (proposal: OfferProposal): string =>
  proposal.consolidatedDescription?.trim() ||
  proposal.scannedDescription?.trim() ||
  '';
